export interface HarmonyInfo {
  isHarmonic: boolean;
  intervalName: string;
  ratio: number;
  beatsPerSec: number;
}

export interface VibratoInfo {
  hasVibrato: boolean;
  depth: number;
  rate: number;
}

const HARMONIC_RATIOS: [number, string][] = [
  [1, 'unison'],
  [9/8, 'major 2nd'],
  [5/4, 'major 3rd'],
  [4/3, 'perfect 4th'],
  [3/2, 'perfect 5th'],
  [5/3, 'major 6th'],
  [15/8, 'major 7th'],
  [2, 'octave'],
];

let pitchHistory: number[] = [];
let lastHarmonyCheck = 0;
const HARMONY_INTERVAL = 200;
const VIBRATO_WINDOW = 20;

export function detectHarmony(
  frequencyData: Float32Array,
  sampleRate: number,
  fftSize: number,
  now: number,
): HarmonyInfo | null {
  if (now - lastHarmonyCheck < HARMONY_INTERVAL) return null;
  lastHarmonyCheck = now;

  const peaks = findTopPeaks(frequencyData, 3);
  if (peaks.length < 2) return null;

  const f1 = (peaks[0].index * sampleRate) / fftSize;
  const f2 = (peaks[1].index * sampleRate) / fftSize;
  if (f1 < 60 || f2 < 60) return null;

  const ratio = f2 / f1;
  let bestMatch = '';
  let bestDist = Infinity;

  for (const [r, name] of HARMONIC_RATIOS) {
    const dist = Math.abs(ratio - r);
    const distInv = Math.abs(ratio - 1/r);
    if (dist < bestDist) { bestDist = dist; bestMatch = name; }
    if (distInv < bestDist) { bestDist = distInv; bestMatch = name; }
  }

  return {
    isHarmonic: bestDist < 0.05,
    intervalName: bestMatch,
    ratio,
    beatsPerSec: Math.abs(f1 - f2),
  };
}

export function detectVibrato(frequency: number): VibratoInfo {
  pitchHistory.push(frequency);
  if (pitchHistory.length > VIBRATO_WINDOW) pitchHistory.shift();

  if (pitchHistory.length < 10) {
    return { hasVibrato: false, depth: 0, rate: 0 };
  }

  const mean = pitchHistory.reduce((a, b) => a + b, 0) / pitchHistory.length;
  const deviations = pitchHistory.map(f => f - mean);
  const depth = Math.sqrt(deviations.reduce((a, d) => a + d * d, 0) / deviations.length);

  let crossings = 0;
  for (let i = 1; i < deviations.length; i++) {
    if ((deviations[i - 1] >= 0 && deviations[i] < 0) ||
        (deviations[i - 1] < 0 && deviations[i] >= 0)) {
      crossings++;
    }
  }

  const windowMs = pitchHistory.length * 16.67;
  const rate = (crossings / 2) / (windowMs / 1000);

  return {
    hasVibrato: depth > 3 && rate > 3 && rate < 12,
    depth,
    rate,
  };
}

function findTopPeaks(data: Float32Array, count: number): { index: number; value: number }[] {
  const peaks: { index: number; value: number }[] = [];
  const minBin = 3;

  for (let i = minBin + 1; i < data.length - 1; i++) {
    if (data[i] > data[i - 1] && data[i] > data[i + 1] && data[i] > -60) {
      peaks.push({ index: i, value: data[i] });
    }
  }

  peaks.sort((a, b) => b.value - a.value);
  return peaks.slice(0, count);
}

export function clearVibratoHistory(): void {
  pitchHistory = [];
}

const SCALE_INTERVALS: Record<string, number[]> = {
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  pentatonic: [0, 2, 4, 7, 9], // C, D, E, G, A
  major: [0, 2, 4, 5, 7, 9, 11], // C, D, E, F, G, A, B
  minor: [0, 2, 3, 5, 7, 8, 10], // C, D, D#, F, G, G#, A#
  blues: [0, 3, 5, 6, 7, 10], // C, D#, F, F#, G, A#
};

export function snapToScale(freq: number, scaleName: string): number {
  if (freq <= 0 || scaleName === 'chromatic') return freq;
  const intervals = SCALE_INTERVALS[scaleName] || SCALE_INTERVALS.major;

  const c4 = 261.63;
  const semitonesFromC4 = 12 * Math.log2(freq / c4);
  const octave = Math.floor(semitonesFromC4 / 12);
  const noteInOctave = ((semitonesFromC4 % 12) + 12) % 12;

  let closestInterval = intervals[0];
  let minDiff = Math.abs(noteInOctave - closestInterval);
  for (const interval of intervals) {
    const diff = Math.abs(noteInOctave - interval);
    const diffWrap = Math.abs((noteInOctave - 12) - interval);
    const diffWrap2 = Math.abs((noteInOctave + 12) - interval);
    const best = Math.min(diff, diffWrap, diffWrap2);
    if (best < minDiff) {
      minDiff = best;
      closestInterval = interval;
    }
  }

  const exactSemitones = octave * 12 + closestInterval;
  return c4 * Math.pow(2, exactSemitones / 12);
}
